import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, ExternalLink } from "lucide-react";

/**
 * Generic editor for landing CMS content.
 * - Bilingual fields {ja, en} are rendered as a 2-column input pair.
 * - Plain string fields are rendered as a single textarea.
 * - Arrays of objects (trust_stats, hud.dimensions, hud.syslog_lines, protocol_section.steps) are
 *   rendered with their own row editors.
 */

const SectionLabel = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3 mt-8 first:mt-0">
    {children}
  </div>
);

const FieldRow = ({ label, children }) => (
  <div className="mb-4">
    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] mb-1.5">
      {label}
    </div>
    {children}
  </div>
);

const Bilingual = ({ value, onChange, multiline = false }) => {
  const update = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const Component = multiline ? "textarea" : "input";
  const cls =
    "w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-2.5 text-sm text-white rounded-md font-body";
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <img src="https://flagcdn.com/24x18/jp.png" width="16" height="12" alt="" />
          <span className="font-mono text-[10px] text-[#64748B]">JA</span>
        </div>
        <Component value={value?.ja || ""} onChange={update("ja")} className={cls} rows={multiline ? 2 : undefined} />
      </div>
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <img src="https://flagcdn.com/24x18/us.png" width="16" height="12" alt="" />
          <span className="font-mono text-[10px] text-[#64748B]">EN</span>
        </div>
        <Component value={value?.en || ""} onChange={update("en")} className={cls} rows={multiline ? 2 : undefined} />
      </div>
    </div>
  );
};

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/landing").then((r) => setContent(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!content) {
    return <div className="p-10 font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// Loading…</div>;
  }

  const setSection = (section, key, val) =>
    setContent({ ...content, [section]: { ...content[section], [key]: val } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/landing", content);
      toast.success("Saved · landing page updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all landing content to defaults?")) return;
    try {
      await api.post("/admin/content/landing/reset");
      const r = await api.get("/content/landing");
      setContent(r.data);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">// CONTENT EDITOR</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight" style={{ fontWeight: 800 }}>
            Landing page CMS
          </h1>
          <p className="text-sm text-[#94A3B8] mt-2">
            Edit every text shown on the homepage. Bilingual JA / EN.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-content-preview"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#1E2A3F] text-[#94A3B8] hover:text-white hover:border-[#3B82F6] rounded-md font-mono text-[11px] uppercase tracking-[0.2em]"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
            Preview
          </a>
          <button
            onClick={reset}
            data-testid="admin-content-reset"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#1E2A3F] text-[#94A3B8] hover:text-[#EF4444] hover:border-[#EF4444] rounded-md font-mono text-[11px] uppercase tracking-[0.2em]"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
            Reset
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-content-save"
            className="btn-gradient inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-md font-mono text-[11px] uppercase tracking-[0.2em] font-bold disabled:opacity-60"
          >
            <Save className="h-3.5 w-3.5" strokeWidth={2} />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        {/* NAVIGATION (header + menus) */}
        <Card title="Navigation (header & menus)">
          {Object.entries(content.nav || {}).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={v} onChange={(nv) => setSection("nav", k, nv)} />
            </FieldRow>
          ))}
        </Card>

        {/* STATUS BAR */}
        <Card title="Status bar (top of hero)">
          {Object.entries(content.status_bar).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={v} onChange={(nv) => setSection("status_bar", k, nv)} />
            </FieldRow>
          ))}
        </Card>

        {/* HERO */}
        <Card title="Hero">
          {Object.entries(content.hero).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={v} onChange={(nv) => setSection("hero", k, nv)} multiline={k.startsWith("subtitle")} />
            </FieldRow>
          ))}
        </Card>

        {/* TRUST STATS */}
        <Card title="Trust stats (3 metrics)">
          {content.trust_stats.map((s, i) => (
            <div key={i} className="bg-[#0A0E1A] border border-[#1E2A3F] rounded p-4 mb-3 last:mb-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-2">
                Stat #{i + 1} · {s.color}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input
                  value={s.value}
                  onChange={(e) => {
                    const arr = [...content.trust_stats];
                    arr[i] = { ...arr[i], value: e.target.value };
                    setContent({ ...content, trust_stats: arr });
                  }}
                  placeholder="Value"
                  className="bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm"
                />
                <input
                  value={s.label_ja}
                  onChange={(e) => {
                    const arr = [...content.trust_stats];
                    arr[i] = { ...arr[i], label_ja: e.target.value };
                    setContent({ ...content, trust_stats: arr });
                  }}
                  placeholder="Label JA"
                  className="bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm"
                />
                <input
                  value={s.label_en}
                  onChange={(e) => {
                    const arr = [...content.trust_stats];
                    arr[i] = { ...arr[i], label_en: e.target.value };
                    setContent({ ...content, trust_stats: arr });
                  }}
                  placeholder="Label EN"
                  className="bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm"
                />
              </div>
            </div>
          ))}
        </Card>

        {/* HUD */}
        <Card title="HUD panels (right side mock)">
          {Object.entries(content.hud)
            .filter(([k]) => !["syslog_lines", "dimensions"].includes(k))
            .map(([k, v]) => (
              <FieldRow key={k} label={k.replace(/_/g, " ")}>
                <Bilingual value={v} onChange={(nv) => setSection("hud", k, nv)} />
              </FieldRow>
            ))}
          <SectionLabel>SYS.LOG lines</SectionLabel>
          {content.hud.syslog_lines.map((line, i) => (
            <input
              key={i}
              value={line}
              onChange={(e) => {
                const arr = [...content.hud.syslog_lines];
                arr[i] = e.target.value;
                setContent({ ...content, hud: { ...content.hud, syslog_lines: arr } });
              }}
              className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 mb-2 rounded text-sm text-white font-mono"
            />
          ))}
        </Card>

        {/* CATEGORIES SECTION */}
        <Card title="Categories section">
          {Object.entries(content.categories_section).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={v} onChange={(nv) => setSection("categories_section", k, nv)} />
            </FieldRow>
          ))}
        </Card>

        {/* CATEGORY LABELS */}
        <Card title="Category labels (5 categories)">
          {Object.entries(content.categories_labels || {}).map(([k, v]) => (
            <FieldRow key={k} label={k}>
              <Bilingual value={v} onChange={(nv) => setSection("categories_labels", k, nv)} />
            </FieldRow>
          ))}
        </Card>

        {/* PROTOCOL SECTION */}
        <Card title="Protocol section (3 steps)">
          {["eyebrow", "title_pre", "title_highlight"].map((k) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={content.protocol_section[k]} onChange={(nv) => setSection("protocol_section", k, nv)} />
            </FieldRow>
          ))}
          <SectionLabel>3 steps</SectionLabel>
          {content.protocol_section.steps.map((s, i) => (
            <div key={i} className="bg-[#0A0E1A] border border-[#1E2A3F] rounded p-4 mb-3 last:mb-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-3">
                Step {String(i + 1).padStart(2, "0")} · {s.color}
              </div>
              <FieldRow label="title">
                <Bilingual
                  value={s.title}
                  onChange={(nv) => {
                    const arr = [...content.protocol_section.steps];
                    arr[i] = { ...arr[i], title: nv };
                    setContent({ ...content, protocol_section: { ...content.protocol_section, steps: arr } });
                  }}
                />
              </FieldRow>
              <FieldRow label="body">
                <Bilingual
                  value={s.body}
                  onChange={(nv) => {
                    const arr = [...content.protocol_section.steps];
                    arr[i] = { ...arr[i], body: nv };
                    setContent({ ...content, protocol_section: { ...content.protocol_section, steps: arr } });
                  }}
                  multiline
                />
              </FieldRow>
            </div>
          ))}
        </Card>

        {/* FINAL CTA */}
        <Card title="Final CTA">
          {Object.entries(content.final_cta).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={v} onChange={(nv) => setSection("final_cta", k, nv)} />
            </FieldRow>
          ))}
        </Card>

        {/* FOOTER */}
        <Card title="Footer">
          {Object.entries(content.footer || {}).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={v} onChange={(nv) => setSection("footer", k, nv)} />
            </FieldRow>
          ))}
        </Card>

        {/* DISCLAIMER */}
        <Card title="Disclaimer / compliance text">
          {Object.entries(content.disclaimer || {}).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")}>
              <Bilingual value={v} onChange={(nv) => setSection("disclaimer", k, nv)} multiline />
            </FieldRow>
          ))}
        </Card>

        {/* ABOUT / OPERATING COMPANY (/about) */}
        <Card title="運営会社 / About page (/about)">
          {content.about && (
            <>
              <FieldRow label="title">
                <Bilingual value={content.about.title} onChange={(nv) => setSection("about", "title", nv)} />
              </FieldRow>
              <FieldRow label="subtitle">
                <Bilingual
                  value={content.about.subtitle}
                  onChange={(nv) => setSection("about", "subtitle", nv)}
                  multiline
                />
              </FieldRow>
              <SectionLabel>Rows ({(content.about.rows || []).length})</SectionLabel>
              <p className="text-xs text-[#94A3B8] mb-4 leading-relaxed">
                Each row is a label / value pair. Email and URL values are auto-linked on the public page.
              </p>
              {(content.about.rows || []).map((row, i) => (
                <div key={i} className="bg-[#0A0E1A] border border-[#1E2A3F] rounded p-4 mb-3 last:mb-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">
                      Row {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={() => {
                          const arr = [...content.about.rows];
                          [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                          setContent({ ...content, about: { ...content.about, rows: arr } });
                        }}
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#3B82F6] disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        disabled={i === (content.about.rows.length - 1)}
                        onClick={() => {
                          const arr = [...content.about.rows];
                          [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
                          setContent({ ...content, about: { ...content.about, rows: arr } });
                        }}
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#3B82F6] disabled:opacity-30"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm(`Remove row "${row.label?.ja || ""}"?`)) return;
                          const arr = [...content.about.rows];
                          arr.splice(i, 1);
                          setContent({ ...content, about: { ...content.about, rows: arr } });
                        }}
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#EF4444] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <FieldRow label="label">
                    <Bilingual
                      value={row.label}
                      onChange={(nv) => {
                        const arr = [...content.about.rows];
                        arr[i] = { ...arr[i], label: nv };
                        setContent({ ...content, about: { ...content.about, rows: arr } });
                      }}
                    />
                  </FieldRow>
                  <FieldRow label="value">
                    <Bilingual
                      value={row.value}
                      onChange={(nv) => {
                        const arr = [...content.about.rows];
                        arr[i] = { ...arr[i], value: nv };
                        setContent({ ...content, about: { ...content.about, rows: arr } });
                      }}
                      multiline
                    />
                  </FieldRow>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const arr = [...(content.about.rows || [])];
                  arr.push({
                    label: { ja: "新しい項目", en: "New row" },
                    value: { ja: "", en: "" },
                  });
                  setContent({ ...content, about: { ...content.about, rows: arr } });
                }}
                className="font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-[#1E2A3F] hover:border-[#3B82F6] hover:text-[#3B82F6] text-[#94A3B8] rounded mb-4"
              >
                + Add row
              </button>
              <FieldRow label="footnote">
                <Bilingual
                  value={content.about.footnote}
                  onChange={(nv) => setSection("about", "footnote", nv)}
                  multiline
                />
              </FieldRow>
            </>
          )}
        </Card>

        {/* TOKUSHOHO (/tokushoho) */}
        <Card title="特定商取引法に基づく表記 (/tokushoho)">
          {content.tokushoho && (
            <>
              <FieldRow label="title">
                <Bilingual value={content.tokushoho.title} onChange={(nv) => setSection("tokushoho", "title", nv)} />
              </FieldRow>
              <FieldRow label="subtitle">
                <Bilingual
                  value={content.tokushoho.subtitle}
                  onChange={(nv) => setSection("tokushoho", "subtitle", nv)}
                  multiline
                />
              </FieldRow>
              <SectionLabel>Rows ({(content.tokushoho.rows || []).length})</SectionLabel>
              <p className="text-xs text-[#94A3B8] mb-4 leading-relaxed">
                Required by Japanese Specified Commercial Transactions Act (特定商取引法 第11条).
                Required fields: 販売事業者・所在地・電話番号・販売価格・支払方法・引渡時期・返品.
              </p>
              {(content.tokushoho.rows || []).map((row, i) => (
                <div key={i} className="bg-[#0A0E1A] border border-[#1E2A3F] rounded p-4 mb-3 last:mb-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">
                      Row {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={() => {
                          const arr = [...content.tokushoho.rows];
                          [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                          setContent({ ...content, tokushoho: { ...content.tokushoho, rows: arr } });
                        }}
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#3B82F6] disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        disabled={i === (content.tokushoho.rows.length - 1)}
                        onClick={() => {
                          const arr = [...content.tokushoho.rows];
                          [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
                          setContent({ ...content, tokushoho: { ...content.tokushoho, rows: arr } });
                        }}
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#3B82F6] disabled:opacity-30"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm(`Remove row "${row.label?.ja || ""}"?`)) return;
                          const arr = [...content.tokushoho.rows];
                          arr.splice(i, 1);
                          setContent({ ...content, tokushoho: { ...content.tokushoho, rows: arr } });
                        }}
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#EF4444] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <FieldRow label="label">
                    <Bilingual
                      value={row.label}
                      onChange={(nv) => {
                        const arr = [...content.tokushoho.rows];
                        arr[i] = { ...arr[i], label: nv };
                        setContent({ ...content, tokushoho: { ...content.tokushoho, rows: arr } });
                      }}
                    />
                  </FieldRow>
                  <FieldRow label="value">
                    <Bilingual
                      value={row.value}
                      onChange={(nv) => {
                        const arr = [...content.tokushoho.rows];
                        arr[i] = { ...arr[i], value: nv };
                        setContent({ ...content, tokushoho: { ...content.tokushoho, rows: arr } });
                      }}
                      multiline
                    />
                  </FieldRow>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const arr = [...(content.tokushoho.rows || [])];
                  arr.push({
                    label: { ja: "新しい項目", en: "New row" },
                    value: { ja: "", en: "" },
                  });
                  setContent({ ...content, tokushoho: { ...content.tokushoho, rows: arr } });
                }}
                className="font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-[#1E2A3F] hover:border-[#3B82F6] hover:text-[#3B82F6] text-[#94A3B8] rounded mb-4"
              >
                + Add row
              </button>
              <FieldRow label="footnote">
                <Bilingual
                  value={content.tokushoho.footnote}
                  onChange={(nv) => setSection("tokushoho", "footnote", nv)}
                  multiline
                />
              </FieldRow>
            </>
          )}
        </Card>

        {/* COMPLIANCE PAGE */}
        <Card title="Compliance page (/compliance)">
          {content.compliance && (
            <>
              <FieldRow label="title">
                <Bilingual
                  value={content.compliance.title}
                  onChange={(nv) => setSection("compliance", "title", nv)}
                />
              </FieldRow>
              <FieldRow label="subtitle">
                <Bilingual
                  value={content.compliance.subtitle}
                  onChange={(nv) => setSection("compliance", "subtitle", nv)}
                  multiline
                />
              </FieldRow>
              <SectionLabel>Regions ({(content.compliance.regions || []).length})</SectionLabel>
              {(content.compliance.regions || []).map((r, i) => (
                <div key={i} className="bg-[#0A0E1A] border border-[#1E2A3F] rounded p-4 mb-3 last:mb-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">
                      {r.code} · {r.law} · {r.color}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm(`Remove ${r.code} (${r.law})?`)) return;
                        const arr = [...content.compliance.regions];
                        arr.splice(i, 1);
                        setContent({
                          ...content,
                          compliance: { ...content.compliance, regions: arr },
                        });
                      }}
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#EF4444] hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                  <FieldRow label="title">
                    <Bilingual
                      value={r.title}
                      onChange={(nv) => {
                        const arr = [...content.compliance.regions];
                        arr[i] = { ...arr[i], title: nv };
                        setContent({
                          ...content,
                          compliance: { ...content.compliance, regions: arr },
                        });
                      }}
                    />
                  </FieldRow>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <input
                      value={r.code || ""}
                      placeholder="Code (EU/US/JP…)"
                      onChange={(e) => {
                        const arr = [...content.compliance.regions];
                        arr[i] = { ...arr[i], code: e.target.value };
                        setContent({
                          ...content,
                          compliance: { ...content.compliance, regions: arr },
                        });
                      }}
                      className="bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm"
                    />
                    <input
                      value={r.law || ""}
                      placeholder="Law (GDPR/CCPA…)"
                      onChange={(e) => {
                        const arr = [...content.compliance.regions];
                        arr[i] = { ...arr[i], law: e.target.value };
                        setContent({
                          ...content,
                          compliance: { ...content.compliance, regions: arr },
                        });
                      }}
                      className="bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm"
                    />
                    <select
                      value={r.color || "blue"}
                      onChange={(e) => {
                        const arr = [...content.compliance.regions];
                        arr[i] = { ...arr[i], color: e.target.value };
                        setContent({
                          ...content,
                          compliance: { ...content.compliance, regions: arr },
                        });
                      }}
                      className="bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm"
                    >
                      <option value="blue">blue</option>
                      <option value="purple">purple</option>
                      <option value="green">green</option>
                    </select>
                  </div>
                  <FieldRow label="items (JA · 1 per line)">
                    <textarea
                      value={(r.items_ja || []).join("\n")}
                      rows={Math.max(2, (r.items_ja || []).length)}
                      onChange={(e) => {
                        const arr = [...content.compliance.regions];
                        arr[i] = {
                          ...arr[i],
                          items_ja: e.target.value.split("\n").filter((x) => x.trim() !== ""),
                        };
                        setContent({
                          ...content,
                          compliance: { ...content.compliance, regions: arr },
                        });
                      }}
                      className="w-full bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm font-body"
                    />
                  </FieldRow>
                  <FieldRow label="items (EN · 1 per line)">
                    <textarea
                      value={(r.items_en || []).join("\n")}
                      rows={Math.max(2, (r.items_en || []).length)}
                      onChange={(e) => {
                        const arr = [...content.compliance.regions];
                        arr[i] = {
                          ...arr[i],
                          items_en: e.target.value.split("\n").filter((x) => x.trim() !== ""),
                        };
                        setContent({
                          ...content,
                          compliance: { ...content.compliance, regions: arr },
                        });
                      }}
                      className="w-full bg-[#070B14] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none p-2 rounded text-white text-sm font-body"
                    />
                  </FieldRow>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const arr = [...(content.compliance.regions || [])];
                  arr.push({
                    code: "XX",
                    law: "LAW",
                    title: { ja: "新しい地域", en: "New region" },
                    items_ja: ["項目1"],
                    items_en: ["Item 1"],
                    color: "blue",
                  });
                  setContent({
                    ...content,
                    compliance: { ...content.compliance, regions: arr },
                  });
                }}
                className="font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-[#1E2A3F] hover:border-[#3B82F6] hover:text-[#3B82F6] text-[#94A3B8] rounded"
              >
                + Add region
              </button>
              <FieldRow label="footnote">
                <Bilingual
                  value={content.compliance.footnote}
                  onChange={(nv) => setSection("compliance", "footnote", nv)}
                  multiline
                />
              </FieldRow>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-[#0F1525] border border-[#1E2A3F] rounded-md p-6 lg:p-8">
      <h2 className="font-display text-2xl tracking-tight mb-1" style={{ fontWeight: 800 }}>
        {title}
      </h2>
      <div className="h-px bg-[#1E2A3F] mb-5 mt-3" />
      {children}
    </section>
  );
}
